package dev.lingvo.site.components.editor

internal data class EditorAnnouncement(
    val id: Long,
    val message: String,
)

internal data class EditorControllerState(
    val domain: TranslationEditorState,
    val workspace: EditorWorkspaceState = EditorWorkspaceState(),
    val pendingEffects: List<EditorViewEffect> = emptyList(),
    val postCommitEvidence: EditorPostCommitEvidenceState = EditorPostCommitEvidenceState(),
    val announcement: EditorAnnouncement? = null,
    val nextAnnouncementId: Long = 1,
)

internal fun applyEditorControllerTransition(
    current: EditorControllerState,
    transition: EditorTransition,
): EditorControllerState {
    val nextWorkspace = transition.state
    val nextEvidence = when {
        nextWorkspace.measuredLayoutDisposition == EditorMeasuredLayoutDisposition.MEASURING &&
            (current.workspace.measuredLayoutDisposition != EditorMeasuredLayoutDisposition.MEASURING ||
                current.workspace.layoutMeasurementId != nextWorkspace.layoutMeasurementId) -> {
            current.postCommitEvidence.beginMeasurement(nextWorkspace.layoutMeasurementId)
        }

        nextWorkspace.measuredLayoutDisposition == EditorMeasuredLayoutDisposition.NOT_APPLICABLE -> {
            EditorPostCommitEvidenceState()
        }

        else -> current.postCommitEvidence
    }
    return current.copy(
        workspace = nextWorkspace,
        pendingEffects = current.pendingEffects + transition.effects,
        postCommitEvidence = nextEvidence,
    )
}

internal fun reduceEditorControllerTransition(
    current: EditorControllerState,
    transition: (EditorWorkspaceState) -> EditorTransition,
): EditorControllerState = applyEditorControllerTransition(
    current = current,
    transition = transition(current.workspace),
)

internal fun abandonCurrentEditorMeasurement(
    current: EditorControllerState,
    expectedMeasurementId: Long,
    contentClientWidth: Double?,
    gutterPx: Double,
): EditorControllerState {
    if (
        current.workspace.layoutMeasurementId != expectedMeasurementId ||
        current.workspace.measuredLayoutDisposition != EditorMeasuredLayoutDisposition.MEASURING
    ) {
        return current
    }
    val abandoned = reduceEditorControllerTransition(current) { latestWorkspace ->
        latestWorkspace.abandonMeasurement(contentClientWidth, gutterPx)
    }
    return abandoned.copy(
        postCommitEvidence = abandoned.postCommitEvidence.beginTerminalAbandonment(
            expectedMeasurementId,
        ),
    )
}

internal fun recordAbandonedEditorMeasurementCommit(
    current: EditorControllerState,
    expectedMeasurementId: Long,
): EditorControllerState {
    if (
        current.workspace.layoutMeasurementId != expectedMeasurementId ||
        current.workspace.measuredLayoutDisposition !in setOf(
            EditorMeasuredLayoutDisposition.WORKBENCH,
            EditorMeasuredLayoutDisposition.COMPACT_FALLBACK,
        )
    ) {
        return current
    }
    val recorded = current.postCommitEvidence.recordAbandonedFallbackCommit(expectedMeasurementId)
    return if (recorded == current.postCommitEvidence) current else current.copy(postCommitEvidence = recorded)
}

internal fun acknowledgeEditorControllerEffect(
    current: EditorControllerState,
    result: EditorViewEffectResult,
): EditorControllerState = if (current.pendingEffects.firstOrNull()?.id == result.effectId) {
    current.copy(pendingEffects = acknowledgeEditorViewEffect(current.pendingEffects, result))
} else {
    current
}

internal fun announceEditorMessage(
    current: EditorControllerState,
    message: String,
): EditorControllerState = current.copy(
    announcement = EditorAnnouncement(current.nextAnnouncementId, message),
    nextAnnouncementId = current.nextAnnouncementId + 1,
)

internal data class EditorCompositionPlan(
    val probes: Int,
    val queues: Int,
    val editors: Int,
    val utilities: Int,
    val scrims: Int,
)

internal fun editorCompositionPlan(
    workspace: EditorWorkspaceState,
    foundationFailed: Boolean,
): EditorCompositionPlan {
    if (foundationFailed) return EditorCompositionPlan(0, 0, 0, 0, 0)
    return when (workspace.renderedLayout()) {
        // **The probe has to render the layout it is going to settle into.** It measures the
        // writing column, so if the probe lays out two tracks and the settled state lays out three,
        // the width it accepted is immediately wrong, the resize observer calls that an external
        // change, and it restarts measurement — forever, with the workspace hidden the whole time.
        // Every branch here must mirror the WORKBENCH branch below exactly.
        EditorRenderedLayout.MEASURING -> when {
            workspace.utility == EditorUtilitySurface.NONE -> EditorCompositionPlan(1, 1, 1, 0, 0)
            workspace.viewportWidth >= EDITOR_TRIO_THRESHOLD_PX ->
                EditorCompositionPlan(1, 1, 1, 1, 0)

            else -> EditorCompositionPlan(1, 0, 1, 1, 0)
        }

        EditorRenderedLayout.WORKBENCH -> when {
            workspace.utility == EditorUtilitySurface.NONE -> EditorCompositionPlan(0, 1, 1, 0, 0)
            // Queue *and* context, where there is room for both beside a full writing measure.
            // Opening a suggestion used to cost the translator the whole key queue; a TM hit is
            // judged against the strings around it, so that was the one thing not to take away.
            workspace.viewportWidth >= EDITOR_TRIO_THRESHOLD_PX ->
                EditorCompositionPlan(0, 1, 1, 1, 0)

            else -> EditorCompositionPlan(0, 0, 1, 1, 0)
        }

        EditorRenderedLayout.COMPACT -> when (workspace.primaryView) {
            EditorPrimaryView.QUEUE -> EditorCompositionPlan(0, 1, 0, 0, 0)
            EditorPrimaryView.EDITOR -> if (workspace.utility == EditorUtilitySurface.NONE) {
                EditorCompositionPlan(0, 0, 1, 0, 0)
            } else {
                EditorCompositionPlan(0, 0, 1, 1, 1)
            }
        }
    }
}

internal fun focusTrapWrapIndex(
    activeIndex: Int,
    focusableCount: Int,
    reverse: Boolean,
): Int? = when {
    focusableCount <= 0 -> null
    reverse && activeIndex <= 0 -> focusableCount - 1
    !reverse && activeIndex >= focusableCount - 1 -> 0
    else -> null
}

internal fun activeEditorTarget(state: TranslationEditorState): EditorFocusRef.Target? {
    val row = state.selectedRow ?: return null
    return EditorFocusRef.Target(
        keyId = row.key.id,
        pluralCategory = state.pluralCategory.takeIf { row.key.isPlural },
        locale = state.locale,
    )
}

internal data class EditorAcceptedMeasurement(
    val id: Long,
    val measurement: EditorWorkbenchMeasurement,
    val workspaceClientWidth: Double,
)
